import { GIF_BASE_PATH, SOUND_BASE_PATH } from './config';

/**
 * Visual/audio effect a reaction produces. Each effect maps to one GIF and one sound.
 * To use real media, drop files with these names into public/assets/gifs and
 * public/assets/sounds. Until then the app falls back to CSS animations and
 * synthesized sounds, so nothing breaks.
 */
export const EFFECT_TYPES = Object.freeze({
  EXPLOSION: 'explosion',
  FIZZ: 'fizz',
  FLASH: 'flash',
  GLOW: 'glow',
  SLOW_CHANGE: 'slow-change',
  MELT: 'melt',
  NONE: 'none',
});

export const EFFECT_GIFS = Object.freeze({
  [EFFECT_TYPES.EXPLOSION]: `${GIF_BASE_PATH}/explosion.gif`,
  [EFFECT_TYPES.FIZZ]: `${GIF_BASE_PATH}/fizz.gif`,
  [EFFECT_TYPES.FLASH]: `${GIF_BASE_PATH}/flash.gif`,
  [EFFECT_TYPES.GLOW]: `${GIF_BASE_PATH}/glow.gif`,
  [EFFECT_TYPES.SLOW_CHANGE]: `${GIF_BASE_PATH}/slow-change.gif`,
  [EFFECT_TYPES.MELT]: `${GIF_BASE_PATH}/melt.gif`,
  [EFFECT_TYPES.NONE]: `${GIF_BASE_PATH}/no-reaction.gif`,
});

export const EFFECT_SOUNDS = Object.freeze({
  [EFFECT_TYPES.EXPLOSION]: `${SOUND_BASE_PATH}/pop.mp3`,
  [EFFECT_TYPES.FIZZ]: `${SOUND_BASE_PATH}/fizz.mp3`,
  [EFFECT_TYPES.FLASH]: `${SOUND_BASE_PATH}/flash.mp3`,
  [EFFECT_TYPES.GLOW]: `${SOUND_BASE_PATH}/glow.mp3`,
  [EFFECT_TYPES.SLOW_CHANGE]: `${SOUND_BASE_PATH}/slow-change.mp3`,
  [EFFECT_TYPES.MELT]: `${SOUND_BASE_PATH}/melt.mp3`,
  [EFFECT_TYPES.NONE]: `${SOUND_BASE_PATH}/boop.mp3`,
});

export const UI_SOUNDS = Object.freeze({
  SELECT: `${SOUND_BASE_PATH}/select.mp3`,
  CLICK: `${SOUND_BASE_PATH}/click.mp3`,
});

export const SUBSTANCE_CATEGORIES = Object.freeze({
  ALKALI_METAL: 'alkali-metal',
  ALKALINE_EARTH_METAL: 'alkaline-earth-metal',
  TRANSITION_METAL: 'transition-metal',
  POST_TRANSITION_METAL: 'post-transition-metal',
  METALLOID: 'metalloid',
  NONMETAL: 'nonmetal',
  HALOGEN: 'halogen',
  NOBLE_GAS: 'noble-gas',
  LANTHANIDE: 'lanthanide',
  ACTINIDE: 'actinide',
  COMPOUND: 'compound',
});

export const CATEGORY_LABELS = Object.freeze({
  [SUBSTANCE_CATEGORIES.ALKALI_METAL]: 'Alkali metals',
  [SUBSTANCE_CATEGORIES.ALKALINE_EARTH_METAL]: 'Alkaline earth metals',
  [SUBSTANCE_CATEGORIES.TRANSITION_METAL]: 'Transition metals',
  [SUBSTANCE_CATEGORIES.POST_TRANSITION_METAL]: 'Other metals',
  [SUBSTANCE_CATEGORIES.METALLOID]: 'Metalloids',
  [SUBSTANCE_CATEGORIES.NONMETAL]: 'Nonmetals',
  [SUBSTANCE_CATEGORIES.HALOGEN]: 'Halogens',
  [SUBSTANCE_CATEGORIES.NOBLE_GAS]: 'Noble gases',
  [SUBSTANCE_CATEGORIES.LANTHANIDE]: 'Lanthanides',
  [SUBSTANCE_CATEGORIES.ACTINIDE]: 'Actinides',
  [SUBSTANCE_CATEGORIES.COMPOUND]: 'Compounds',
});

// Liquid colour inside the flasks for each category (matches the tile colours, a bit stronger).
export const CATEGORY_LIQUID_COLORS = Object.freeze({
  [SUBSTANCE_CATEGORIES.ALKALI_METAL]: '#ff8a78',
  [SUBSTANCE_CATEGORIES.ALKALINE_EARTH_METAL]: '#ffc15e',
  [SUBSTANCE_CATEGORIES.TRANSITION_METAL]: '#7fb6ff',
  [SUBSTANCE_CATEGORIES.POST_TRANSITION_METAL]: '#8fd3b4',
  [SUBSTANCE_CATEGORIES.METALLOID]: '#b5c96a',
  [SUBSTANCE_CATEGORIES.NONMETAL]: '#9be070',
  [SUBSTANCE_CATEGORIES.HALOGEN]: '#e8e25a',
  [SUBSTANCE_CATEGORIES.NOBLE_GAS]: '#c79bff',
  [SUBSTANCE_CATEGORIES.LANTHANIDE]: '#ff9ccf',
  [SUBSTANCE_CATEGORIES.ACTINIDE]: '#ff7fae',
  [SUBSTANCE_CATEGORIES.COMPOUND]: '#6fd6e6',
});

// Beaker liquid colour after mixing, by effect.
export const EFFECT_LIQUID_COLORS = Object.freeze({
  [EFFECT_TYPES.EXPLOSION]: '#c77dff',
  [EFFECT_TYPES.FIZZ]: '#9ad9ff',
  [EFFECT_TYPES.FLASH]: '#fff4a8',
  [EFFECT_TYPES.GLOW]: '#ffb347',
  [EFFECT_TYPES.SLOW_CHANGE]: '#b5582a',
  [EFFECT_TYPES.MELT]: '#c9d1dc',
  [EFFECT_TYPES.NONE]: '#b9c7d8',
});

export const EMPTY_LIQUID_COLOR = '#dff3ff';

export const KID_EXPRESSIONS = Object.freeze({
  HAPPY: 'happy',
  FRIGHTENED: 'frightened',
});

// Reactions that make Fizzy jump while they play.
export const FRIGHTENING_EFFECTS = Object.freeze([EFFECT_TYPES.EXPLOSION]);

// Fizzy's mouth shapes while talking; CLOSED is the resting smile.
export const MOUTH_SHAPES = Object.freeze({
  CLOSED: 'closed',
  OVAL: 'oval',
  TEETH: 'teeth',
});
